// منوی راهنما و پشتیبانی Phoenix در Header (US-CHN-045): ثبت تیکت با توضیح، تصویر صفحه (پیوست کاربر)، نسخه اپ، شناسه همبستگی
// آخرین خطا و مسیر صفحه بدون شناسه‌ها (هیچ داده بالینی یا هویتی مراجعه‌کننده خودکار ضمیمه نمی‌شود — AC-CHN-045.1)؛ در حالت
// ReadOnly هم مجاز است (BR-PLT-006). «تیکت‌های من» فقط تیکت‌های همین کاربر با گفت‌وگو، پاسخ و بستن.
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  getLastErrorTraceId,
  unwrap,
  type SupportTicketDetail,
  type SupportTicketListItem,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDate, KeyValueList, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { APP_VERSION } from '../config';

export const TICKET_CATEGORIES = ['bug', 'question', 'request'] as const;
export const SCREENSHOT_MAX_BYTES = 2 * 1024 * 1024;
const SCREENSHOT_TYPES = ['image/png', 'image/jpeg'];
const ticketKeys = {
  mine: (tenantId: string | undefined) => ['channels', 'support-tickets', tenantId] as const,
  detail: (tenantId: string | undefined, id: string) =>
    ['channels', 'support-tickets', tenantId, id] as const,
};

/** مسیر صفحه بدون شناسه‌ها و query (هم‌ارز SupportTicket.SanitizeRoute سرور). */
export function sanitizeRoute(pathname: string): string {
  return pathname
    .split('/')
    .map((segment) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
      /^\d{4,}$/.test(segment)
        ? ':id'
        : segment,
    )
    .join('/');
}

function NewTicketDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (ticket: SupportTicketDetail) => void;
}) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const location = useLocation();
  const titleId = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<string>('bug');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [route] = useState(() => sanitizeRoute(location.pathname));
  const [traceId] = useState(() => getLastErrorTraceId() ?? '');

  const create = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('category', category);
      form.append('subject', subject.trim());
      form.append('description', description.trim());
      form.append('appVersion', APP_VERSION);
      form.append('routePath', route);
      if (traceId) {
        form.append('traceId', traceId);
      }
      if (screenshot) {
        form.append('screenshot', screenshot, screenshot.name);
      }
      return unwrap(
        tenantApi.POST('/api/v1/channels/support-tickets', {
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
      );
    },
    onSuccess: async (ticket) => {
      await queryClient.invalidateQueries({ queryKey: ticketKeys.mine(session?.tenantId) });
      onCreated(ticket);
    },
  });

  const invalid = !subject.trim() || !description.trim();
  return (
    <Dialog
      open
      onClose={create.isPending ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>{t('chn:support.new_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label={t('chn:support.category')}
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            fullWidth
          >
            {TICKET_CATEGORIES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`chn:support.categories.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('chn:support.subject')}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            required
            fullWidth
            error={touched && !subject.trim()}
            helperText={touched && !subject.trim() ? t('common:validation.required') : undefined}
            slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'ticket-subject' } }}
          />
          <TextField
            label={t('chn:support.description')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            required
            fullWidth
            multiline
            minRows={4}
            error={touched && !description.trim()}
            helperText={
              touched && !description.trim()
                ? t('common:validation.required')
                : t('chn:support.no_patient_data')
            }
            slotProps={{ htmlInput: { maxLength: 4000, 'data-testid': 'ticket-description' } }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg"
              hidden
              aria-label={t('chn:support.screenshot')}
              data-testid="ticket-screenshot"
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                event.target.value = '';
                if (file && (!SCREENSHOT_TYPES.includes(file.type) || file.size > SCREENSHOT_MAX_BYTES)) {
                  setFileError(t('chn:support.screenshot_invalid'));
                  setScreenshot(null);
                  return;
                }
                setFileError(null);
                setScreenshot(file);
              }}
            />
            <Button variant="outlined" onClick={() => fileInput.current?.click()}>
              {t('chn:support.attach_screenshot')}
            </Button>
            {screenshot && (
              <Chip
                label={screenshot.name}
                onDelete={() => setScreenshot(null)}
                data-testid="ticket-screenshot-name"
              />
            )}
          </Stack>
          {fileError && <Alert severity="error">{fileError}</Alert>}
          <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="ticket-technical">
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              {t('chn:support.technical_help')}
            </Typography>
            <KeyValueList
              columns={1}
              items={[
                { labelKey: 'chn:support.app_version', value: <bdi dir="ltr">{APP_VERSION}</bdi> },
                {
                  labelKey: 'chn:support.route',
                  value: <bdi dir="ltr">{route}</bdi>,
                  testId: 'ticket-route',
                },
                {
                  labelKey: 'chn:support.trace_id',
                  value: traceId ? <bdi dir="ltr">{traceId}</bdi> : t('chn:support.no_trace'),
                  testId: 'ticket-trace',
                },
              ]}
            />
          </Paper>
          {create.error ? <ErrorState error={create.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={create.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            setTouched(true);
            if (!invalid) {
              create.mutate();
            }
          }}
          disabled={create.isPending}
          data-testid="ticket-submit"
        >
          {t('chn:support.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TicketConversation({ id }: { id: string }) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const detail = useQuery({
    queryKey: ticketKeys.detail(session?.tenantId, id),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/channels/support-tickets/{id}', { params: { path: { id } } })),
  });
  const onUpdated = async (ticket: SupportTicketDetail) => {
    queryClient.setQueryData(ticketKeys.detail(session?.tenantId, id), ticket);
    await queryClient.invalidateQueries({ queryKey: ticketKeys.mine(session?.tenantId), exact: true });
  };
  const send = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/channels/support-tickets/{id}/messages', {
          params: { path: { id } },
          body: { body: reply.trim() },
        }),
      ),
    onSuccess: async (ticket) => {
      setReply('');
      await onUpdated(ticket);
    },
  });
  const close = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/channels/support-tickets/{id}:close', { params: { path: { id } } })),
    onSuccess: onUpdated,
  });

  if (detail.isPending) {
    return <LoadingState rows={2} />;
  }
  if (detail.error || !detail.data) {
    return <ErrorState error={detail.error} compact />;
  }
  const ticket = detail.data;
  const closed = ticket.status === 'Closed';
  return (
    <Stack spacing={1.5} data-testid="ticket-conversation">
      <Typography variant="subtitle1">
        #{faDigits(String(ticket.number))} · {ticket.subject}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {ticket.description}
      </Typography>
      {ticket.messages.map((message) => (
        <Paper
          key={message.id}
          variant="outlined"
          sx={{ p: 1, bgcolor: message.author === 'Support' ? 'action.hover' : undefined }}
        >
          <Typography variant="caption" color="text.secondary">
            {message.authorName} · <JalaliDate value={message.createdAt} withTime />
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.body}
          </Typography>
        </Paper>
      ))}
      {!closed && (
        <>
          <TextField
            label={t('chn:support.reply')}
            value={reply}
            onChange={(event) => setReply(event.target.value)}
            multiline
            minRows={2}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 4000, 'data-testid': 'ticket-reply' } }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={() => send.mutate()}
              disabled={!reply.trim() || send.isPending}
              data-testid="ticket-reply-send"
            >
              {t('chn:support.send')}
            </Button>
            <Button onClick={() => close.mutate()} disabled={close.isPending} data-testid="ticket-close">
              {t('chn:support.close_ticket')}
            </Button>
          </Stack>
        </>
      )}
      {send.error || close.error ? <ErrorState error={send.error ?? close.error} compact /> : null}
    </Stack>
  );
}

function MyTicketsDialog({ initial, onClose }: { initial: string | null; onClose: () => void }) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const titleId = useId();
  const [selected, setSelected] = useState<string | null>(initial);
  const tickets = useQuery({
    queryKey: ticketKeys.mine(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/support-tickets')),
  });

  const label = (ticket: SupportTicketListItem) => `#${faDigits(String(ticket.number))} · ${ticket.subject}`;
  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="md" fullWidth>
      <DialogTitle id={titleId}>{t('chn:support.mine_title')}</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ minWidth: 260 }}>
            {tickets.isPending ? (
              <LoadingState rows={2} />
            ) : tickets.error ? (
              <ErrorState error={tickets.error} compact />
            ) : (tickets.data?.items.length ?? 0) === 0 ? (
              <Typography color="text.secondary">{t('chn:support.mine_empty')}</Typography>
            ) : (
              <List dense aria-label={t('chn:support.mine_title')} data-testid="my-tickets">
                {tickets.data?.items.map((ticket) => (
                  <ListItemButton
                    key={ticket.id}
                    selected={ticket.id === selected}
                    onClick={() => setSelected(ticket.id)}
                    data-testid={`my-ticket-${ticket.number}`}
                  >
                    <ListItemText
                      primary={label(ticket)}
                      secondary={
                        <>
                          {t(`chn:support.status.${ticket.status}`)}
                          {ticket.awaitingRequester ? ` · ${t('chn:support.awaiting_you')}` : ''}
                        </>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>{selected && <TicketConversation key={selected} id={selected} />}</Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function SupportMenu() {
  const { t } = useTranslation('chn');
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [dialog, setDialog] = useState<'new' | 'mine' | null>(null);
  const [created, setCreated] = useState<SupportTicketDetail | null>(null);

  return (
    <>
      <Tooltip title={t('chn:support.menu')}>
        <IconButton
          color="inherit"
          aria-label={t('chn:support.menu')}
          aria-haspopup="menu"
          onClick={(event) => setAnchor(event.currentTarget)}
          data-testid="support-menu"
        >
          <HelpOutlineIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setCreated(null);
            setDialog('new');
          }}
          data-testid="support-ticket-new"
        >
          {t('chn:support.new')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setDialog('mine');
          }}
          data-testid="support-ticket-mine"
        >
          {t('chn:support.mine')}
        </MenuItem>
      </Menu>
      {dialog === 'new' && (
        <NewTicketDialog
          onClose={() => setDialog(null)}
          onCreated={(ticket) => {
            setCreated(ticket);
            setDialog('mine');
          }}
        />
      )}
      {dialog === 'mine' && (
        <MyTicketsDialog
          initial={created?.id ?? null}
          onClose={() => {
            setDialog(null);
            setCreated(null);
          }}
        />
      )}
    </>
  );
}
